<template>
    <vue-editor ref="quillEditor" :editorOptions="editorSettings" v-model="editorContent" :disabled="disabled" :id="id" :placeholder="placeholder"  :class="{hideToolBar:hideBar}"></vue-editor>
</template>

<script>
import './style.css';
import { VueEditor, Quill } from 'vue2-editor';

const BlockEmbed = Quill.import('blots/block/embed');
let Parchment = Quill.import('parchment');

// 字号
const Size = Quill.import('attributors/class/size');
Size.whitelist = ['12', '14', '16', '18', '20', '24', '28', '32', '36'];
Quill.register(Size, true);

// 行高
const lineHeightClass = new Parchment.Attributor.Class('lineHeight', 'ql-lineHeight', {
    scope: Parchment.Scope.INLINE,
    whitelist: ['1', '1-5', '1-75', '2', '3', '4', '5']
});
Quill.register(lineHeightClass, true);

// 字间距
const letterSpacingClass = new Parchment.Attributor.Class('letterSpacing', 'ql-letterSpacing', {
    scope: Parchment.Scope.INLINE,
    whitelist: ['0', '0-5', '1', '2']
});
Quill.register(letterSpacingClass, true);

// HR
class Hr extends BlockEmbed {
    static create(value) {
        let node = super.create(value);
        return node;
    }
}
Hr.blotName = 'hr';
Hr.className = 'ql-hr';
Hr.tagName = 'hr';
Quill.register({
    'formats/hr': Hr
});

export default {
    name: 'RichTextEditor',
    components: {
        VueEditor
    },
    props: {
        value: String,
        id: {
            type: String,
            default: 'quill-container'
        },
        placeholder: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        },
        colors: {
            type: Array,
            default: function() {
                return [];
            }
        },
        hideBar: {
            type: Boolean,
            default: false
        }
    },
    data() {
        let editorToolbar = [
            [
                { size: ['12', '14', false, '18', '20', '24', '28', '32', '36'] },
                { lineHeight: ['1', false, '1-75', '2', '3', '4', '5'] },
                { letterSpacing: ['0', '0-5', '1', '2'] },
                { header: 1 },
                { header: 2 }
            ],
            ['bold', 'italic', 'underline', 'strike'],
            [{ color: this.colors || [] }, { background: this.colors || [] }],
            ['blockquote'],
            [{ hr: null }],
            [{ list: 'bullet' }, { list: 'ordered' }],
            [{ indent: '-1' }, { indent: '+1' }],
            [{ align: [] }],
            ['clean']
        ];
        return {
            editorSettings: {
                modules: {
                    toolbar: editorToolbar
                }
            }
        };
    },
    computed: {
        editorContent: {
            get: function() {
                return this.value;
            },
            set: function(val) {
                this.$emit('input', val);
            }
        }
    },
    mounted() {
        // 插入HR逻辑
        const quill = this.$refs.quillEditor.quill;
        let customHrHandler = function() {
            let range = quill.getSelection(true);
            quill.insertText(range.index, '\n', Quill.sources.USER);
            quill.insertEmbed(range.index + 1, 'hr', true, Quill.sources.USER);
            quill.setSelection(range.index + 2, Quill.sources.SILENT);
        };
        let toolbar = quill.getModule('toolbar');
        toolbar.addHandler('hr', customHrHandler);
    }
};
</script>

<style lang="scss">
.ql-snow .ql-editor {
    line-height: 1.5;
}
.ql-snow .ql-picker.ql-lineHeight,
.ql-snow .ql-picker.ql-letterSpacing {
    width: 80px;
}
.ql-snow .ql-picker.ql-size::before,
.ql-snow .ql-picker.ql-lineHeight::before,
.ql-snow .ql-picker.ql-letterSpacing::before {
    position: absolute;
    height: 30px;
    width: 100%;
    line-height: 30px;
    bottom: -30px;
    left: 0;
    background: #000;
    color: #ffffff;
    text-align: center;
    display: none;
}
.ql-snow .ql-picker.ql-size:hover::before,
.ql-snow .ql-picker.ql-lineHeight:hover::before,
.ql-snow .ql-picker.ql-letterSpacing:hover::before {
    display: block;
}
.ql-snow .ql-picker.ql-size::before {
    content: '字号';
}
.ql-toolbar.ql-snow .ql-picker.ql-size .ql-picker-item::before,
.ql-toolbar.ql-snow .ql-picker.ql-size .ql-picker-label:before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value='16']::before,
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value='16']:before {
    content: '16';
    font-size: 16px !important;
}
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value='12']::before,
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value='12']:before {
    content: '12';
    font-size: 12px !important;
}
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value='14']::before,
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value='14']:before {
    content: '14';
    font-size: 14px !important;
}
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value='18']::before,
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value='18']:before {
    content: '18';
    font-size: 18px !important;
}
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value='20']::before,
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value='20']:before {
    content: '20';
    font-size: 20px !important;
}
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value='24']::before,
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value='24']:before {
    content: '24';
    font-size: 24px !important;
}
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value='28']::before,
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value='28']:before {
    content: '28';
    font-size: 28px !important;
}
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value='32']::before,
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value='32']:before {
    content: '32';
    font-size: 32px !important;
}
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value='36']::before,
.ql-snow .ql-picker.ql-size .ql-picker-label[data-value='36']:before {
    content: '36';
    font-size: 36px !important;
}
.ql-snow .ql-picker.ql-lineHeight::before {
    content: '行间距';
}
.ql-toolbar.ql-snow .ql-picker.ql-lineHeight .ql-picker-item::before,
.ql-toolbar.ql-snow .ql-picker.ql-lineHeight .ql-picker-label:before,
.ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value='1-5']::before,
.ql-snow .ql-picker.ql-lineHeight .ql-picker-label[data-value='1-5']:before {
    content: '1.5';
}
.ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value='1']::before,
.ql-snow .ql-picker.ql-lineHeight .ql-picker-label[data-value='1']:before {
    content: '1';
}
.ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value='1-75']::before,
.ql-snow .ql-picker.ql-lineHeight .ql-picker-label[data-value='1-75']:before {
    content: '1.75';
}
.ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value='2']::before,
.ql-snow .ql-picker.ql-lineHeight .ql-picker-label[data-value='2']:before {
    content: '2';
}
.ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value='3']::before,
.ql-snow .ql-picker.ql-lineHeight .ql-picker-label[data-value='3']:before {
    content: '3';
}
.ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value='4']::before,
.ql-snow .ql-picker.ql-lineHeight .ql-picker-label[data-value='4']:before {
    content: '4';
}
.ql-snow .ql-picker.ql-lineHeight .ql-picker-item[data-value='5']::before,
.ql-snow .ql-picker.ql-lineHeight .ql-picker-label[data-value='5']:before {
    content: '5';
}
.ql-snow .ql-picker.ql-letterSpacing::before {
    content: '字间距';
}
.ql-snow .ql-picker.ql-letterSpacing .ql-picker-item[data-value='0']::before,
.ql-snow .ql-picker.ql-letterSpacing .ql-picker-label[data-value='0']:before {
    content: '默认';
}
.ql-snow .ql-picker.ql-letterSpacing .ql-picker-item[data-value='0-5']::before,
.ql-snow .ql-picker.ql-letterSpacing .ql-picker-label[data-value='0-5']:before {
    content: '0.5';
}
.ql-snow .ql-picker.ql-letterSpacing .ql-picker-item[data-value='1']::before,
.ql-snow .ql-picker.ql-letterSpacing .ql-picker-label[data-value='1']:before {
    content: '1';
}
.ql-snow .ql-picker.ql-letterSpacing .ql-picker-item[data-value='2']::before,
.ql-snow .ql-picker.ql-letterSpacing .ql-picker-label[data-value='2']:before {
    content: '2';
}
.ql-snow.ql-toolbar button.ql-hr::before {
    content: '—';
    display: block;
    font-weight: bold;
}
.hideToolBar.quillWrapper {
    .ql-snow.ql-toolbar {
        display: none;
    }
    .ql-container.ql-snow {
        border: 0px;
    }
}
</style>
